import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/utils-tg";
import { Loader2, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("password"); // "password" | "magic"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "password") {
        await login(email, password);
        nav("/");
      } else {
        await api.post("/auth/magic/request", { email });
        setMagicSent(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || (mode === "magic" ? "Couldn't send link" : "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-8">
          <img src={LOGO_URL} className="h-20 w-auto object-contain" alt="Talk & Go" />
        </div>

        <div className="tg-card p-8">
          {magicSent ? (
            <div data-testid="magic-sent-screen">
              <div className="h-12 w-12 rounded-full bg-emerald-50 text-emerald-600 mx-auto mb-4 flex items-center justify-center">
                <Mail className="h-6 w-6" />
              </div>
              <h1 className="font-display text-xl font-bold text-slate-900 text-center">Check your inbox</h1>
              <p className="text-sm text-slate-500 mt-2 text-center">
                If <span className="font-semibold">{email}</span> is registered, a sign-in link is on its way. The link expires in 15 minutes.
              </p>
              <button
                onClick={() => { setMagicSent(false); setMode("password"); }}
                className="tg-btn-secondary w-full mt-6"
                data-testid="magic-back"
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-bold text-slate-900">Welcome back</h1>
              <p className="text-sm text-slate-500 mt-1 mb-4">Sign in to keep your work moving.</p>

              <div className="flex gap-2 mb-5 bg-slate-100 p-1 rounded-xl" data-testid="login-mode-toggle">
                <button
                  type="button"
                  onClick={() => setMode("password")}
                  className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-1.5 ${
                    mode === "password" ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid="mode-password"
                >
                  <KeyRound className="h-3.5 w-3.5" /> Password
                </button>
                <button
                  type="button"
                  onClick={() => setMode("magic")}
                  className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-1.5 ${
                    mode === "magic" ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid="mode-magic"
                >
                  <Mail className="h-3.5 w-3.5" /> Magic link
                </button>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Email</label>
                  <input
                    type="email"
                    required
                    className="tg-input mt-1"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="alex@company.com"
                    data-testid="login-email-input"
                  />
                </div>
                {mode === "password" && (
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Password</label>
                    <input
                      type="password"
                      required
                      className="tg-input mt-1"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      data-testid="login-password-input"
                    />
                  </div>
                )}
                <button type="submit" disabled={busy} className="tg-btn-primary w-full" data-testid="login-submit">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {mode === "password" ? "Sign in" : "Email me a link"}
                </button>
                {mode === "magic" && (
                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    We'll email you a link that signs you in for 15 minutes. No password needed.
                  </p>
                )}
              </form>

              <p className="text-sm text-slate-500 mt-6 text-center">
                New here?{" "}
                <Link to="/register" className="text-emerald-600 font-semibold hover:underline" data-testid="link-register">
                  Create an account
                </Link>
              </p>
            </>
          )}
        </div>

        <p className="text-xs text-slate-400 text-center mt-6">
          Turn conversations into work — instantly.
        </p>
      </div>
    </div>
  );
}
